import { ReactNode, MouseEvent } from 'react';

interface ModalScrollContainerProps {
  children: ReactNode;
  /** Extra classes for the OUTER (fixed) element — z-index, backdrop colour, blur. */
  className?: string;
  /** Backdrop click handler. Safe on the outer element: a click on the inner
   *  padding bubbles up, and panels that must not close already stopPropagation. */
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  role?: string;
  'aria-modal'?: boolean | 'true' | 'false';
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * The scroll container every centred modal sits in.
 *
 * 🔴 The shape this replaces was `fixed inset-0 flex items-center justify-center
 * p-4` — centred, but with no overflow and no max height. A panel taller than
 * the window then put its own Submit button off-screen with no way to reach it,
 * and there is no scrollbar to get there because the only scrollable thing on
 * the page is behind the backdrop. MaintenancePage's Create Work Order modal
 * crossed that line on a laptop when the Asset field was added, and the live
 * check failed with "element is outside of the viewport": the picker worked
 * perfectly and the work order could not be submitted.
 *
 * Two parts, and BOTH are load-bearing:
 *
 *   overflow-y-auto on the fixed element  — gives the overflow somewhere to go.
 *   min-h-full on the inner flex          — keeps it centred when it DOES fit.
 *
 * The inner wrapper is not decoration. `align-items: center` on a scroll
 * container clips an over-tall flex item at the TOP and makes that part
 * unreachable — scrolling up stops at the container's own edge. Centring on an
 * inner `min-h-full` child instead means the child grows past the container
 * when the content is tall, so the scroll range covers the whole panel, while a
 * short panel still centres because min-h-full fills the viewport.
 *
 * This is the same container Headless UI's own Dialog examples use, and the
 * same one FormModal and ConfirmDialog already had — those two were never
 * affected. It is extracted here because the modals that hand-roll their
 * container are the ones that got it wrong, and they got it wrong identically.
 *
 * Works for both families without changing dialog semantics:
 *
 *   Headless UI:  <ModalScrollContainer>          <Dialog.Panel .../>
 *   Hand-rolled:  <ModalScrollContainer className="z-50 bg-scrim/50">  <div .../>
 *
 * It deliberately does NOT add a focus trap or Escape handling to the
 * hand-rolled ones. That would be a real improvement and a different change;
 * this one is about reaching the button.
 */
export default function ModalScrollContainer({
  children,
  className = '',
  onClick,
  ...rest
}: ModalScrollContainerProps) {
  return (
    <div className={`fixed inset-0 overflow-y-auto ${className}`} onClick={onClick} {...rest}>
      <div className="flex min-h-full items-center justify-center p-4">{children}</div>
    </div>
  );
}
